import { useContext, useEffect } from "react";
import { AppContext } from "../../../context/app";
import { MinimalCalendarItem, getAllTodaysCalendarItems } from "../../../apis/calendarApi";
import { getTimeValueForSort } from "../MySchedule/dailyScheduleUtils";
import { useGetAllTodaysCalendarItemsQuery, TODAYS_CALENDAR_ITEMS_QUERY_KEY } from "../../../apis/calendarApi.hooks";
import { formatInTimeZone } from "date-fns-tz";
import { parseISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";

export type ClassScheduleItem = {
  id: string;
  startTime: Date;
  endTime: Date;
  formattedStartTime?: string;
  formattedEndTime?: string;
  title: string;
  link?: string;
  attendanceCount?: number;
  type: "classScheduleItem";
  data: MinimalCalendarItem;
}

export type TodaysClasses = {
  todaysClasses: ClassScheduleItem[];
  todaysClassesLoading: boolean;
}

export const useTodaysClasses = (): TodaysClasses => {
  const { client } = useContext(AppContext);

  const { data: calendarItems, isLoading: todaysClassesLoading } =
    useGetAllTodaysCalendarItemsQuery();

  const todaysClasses = calendarItems?.map((item: MinimalCalendarItem) => {
    const startDateTime = parseISO(item.startDateTime);
    const endDateTime = parseISO(item.endDateTime);

    return ({
      id: item.uuid,
      startTime: startDateTime,
      endTime: endDateTime,
      formattedStartTime: formatInTimeZone(startDateTime, "UTC", "h:mmaaa"),
      formattedEndTime: formatInTimeZone(endDateTime, "UTC", "h:mmaaa"),
      title: item.title,
      type: "classScheduleItem" as const,
      data: item,
    })
  }).sort((a, b) => getTimeValueForSort(a.startTime, client.timezone, true) - getTimeValueForSort(b.startTime, client.timezone, true)) || [];

  return {
    todaysClasses,
    todaysClassesLoading,
  }
}

export function usePrefetchTodaysClasses() {
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: TODAYS_CALENDAR_ITEMS_QUERY_KEY,
      queryFn: () => getAllTodaysCalendarItems(),
      staleTime: 1000 * 60 * 60,
      cacheTime: 1000 * 60 * 10,
    });
  }, [queryClient]);
}

